import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { ScrollText, Search, RefreshCw, Loader2, ShieldCheck } from "lucide-react";

const METHOD_CLS = {
  POST: "bg-emerald-50 text-emerald-700",
  PUT: "bg-amber-50 text-amber-700",
  PATCH: "bg-amber-50 text-amber-700",
  DELETE: "bg-red-50 text-red-700",
};

const fmtTs = (ts) => {
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [scoped, setScoped] = useState(false);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 50;

  const load = useCallback((reset = true) => {
    setLoading(true);
    const off = reset ? 0 : offset;
    api.get(`/audit-log?limit=${LIMIT}&offset=${off}&q=${encodeURIComponent(q)}`)
      .then(({ data }) => {
        setCount(data.count || 0);
        setScoped(!!data.scoped);
        setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        setOffset(off + (data.items?.length || 0));
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load audit log"))
      .finally(() => setLoading(false));
  }, [q, offset]);

  // initial + on search
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);
  const search = (e) => { e.preventDefault(); load(true); };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <Seo title="Audit log" description="Tamper-evident record of every change in ERIO." path="/audit-log" />
      <main id="main-content" className="mx-auto max-w-6xl px-6 py-8" data-testid="audit-log-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="ek-eyebrow">Governance · Accountability</p>
            <h1 className="ek-heading mt-1 text-3xl">Audit log</h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="/briefing-ledger" data-testid="audit-nav-briefing-ledger" className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5">
              AI Briefing Ledger →
            </a>
            <button onClick={() => load(true)} aria-label="Refresh audit log" className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium text-ek-ink hover:bg-ek-purple/5" data-testid="audit-refresh">
              <RefreshCw size={15} /> Refresh
            </button>
          </div>
        </div>

        <IntroNote className="mt-4">
          Every state-changing action is recorded here with the actor, route, outcome and time — a tamper-evident
          trail for accountability and incident investigation. {scoped ? "You're viewing your tenant's activity." : "As Owner, you're viewing platform-wide activity."}
        </IntroNote>

        <form onSubmit={search} className="mt-6 flex items-center gap-2" data-testid="audit-search-form">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-2.5 text-ek-ink/30" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by route, e.g. /api/risks"
              data-testid="audit-search-input"
              className="w-full rounded-lg border border-black/10 py-2 pl-9 pr-3 text-sm"
            />
          </div>
          <button type="submit" className="ek-btn px-4 py-2 text-sm font-semibold" data-testid="audit-search-submit">Search</button>
        </form>

        <p className="mt-3 text-xs text-ek-ink/65" data-testid="audit-count">{count} matching entries</p>

        <div className="mt-3 overflow-hidden rounded-xl border border-black/5 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 bg-ek-canvas text-left text-xs uppercase tracking-wide text-ek-ink/65">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">Actor</th>
                <th className="px-4 py-3 font-semibold">Method</th>
                <th className="px-4 py-3 font-semibold">Route</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="hidden px-4 py-3 font-semibold md:table-cell">IP</th>
              </tr>
            </thead>
            <tbody data-testid="audit-rows">
              {items.map((r) => (
                <tr key={r.audit_id} className="border-b border-black/5 last:border-0" data-testid="audit-row">
                  <td className="whitespace-nowrap px-4 py-2.5 text-ek-ink/70">{fmtTs(r.ts)}</td>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-ek-ink">{r.actor_role || "—"}</span>
                    <span className="ml-1 font-mono text-[11px] text-ek-ink/65">{(r.actor_user_id || "anon").slice(0, 12)}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${METHOD_CLS[r.method] || "bg-ek-canvas text-ek-ink/60"}`}>{r.method}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[12px] text-ek-ink/70">{r.route}</td>
                  <td className="px-4 py-2.5">
                    <span className={r.status_code >= 400 ? "text-red-700 font-semibold" : "text-emerald-700"}>{r.status_code}</span>
                  </td>
                  <td className="hidden px-4 py-2.5 font-mono text-[11px] text-ek-ink/65 md:table-cell">{r.client_ip}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ek-ink/65" data-testid="audit-empty">No audit entries match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {loading && <div className="mt-4 flex justify-center"><Loader2 className="animate-spin text-ek-purple" /></div>}
        {!loading && items.length < count && (
          <div className="mt-4 flex justify-center">
            <button onClick={() => load(false)} className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-4 py-2 text-sm font-medium hover:bg-ek-purple/5" data-testid="audit-load-more">
              Load more
            </button>
          </div>
        )}
        <p className="mt-6 inline-flex items-center gap-1 text-[11px] text-emerald-700">
          <ShieldCheck size={12} /> Entries are append-only and hash-stamped.
        </p>
      </main>
    </div>
  );
}
